import { Link, useParams } from "react-router-dom";
import {
  PageHeader,
  Panel,
  EmptyState,
  LoadingPanel,
  ErrorPanel,
  StatusChip,
} from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerOffer } from "../../api/endpoints/program";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import { offerDeadline, offerReward, offerTarget } from "../Dashboard/offerHelpers";

const BackLink = () => (
  <Link
    to="/partner/offers"
    className="inline-block mb-4 text-xs font-semibold text-teal-700 no-underline hover:text-teal-800"
  >
    ← All offers
  </Link>
);

const Fact = ({ label, value }) =>
  value ? (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0">
        {label}
      </p>
      <p className="text-sm font-semibold text-slate-900 m-0 mt-1">{value}</p>
    </div>
  ) : null;

/** Everything the office wrote about one offer, running or ended: lists only show the start of it. */
const OfferDetailPage = () => {
  const { offerId } = useParams();
  const {
    data: offer,
    isLoading,
    isError,
    error,
    refetch,
  } = usePartnerQuery(partnerQueryKeys.offer(offerId), () => fetchPartnerOffer(offerId), {
    retry: false,
  });

  if (isLoading) {
    return (
      <div>
        <BackLink />
        <LoadingPanel label="Loading the offer…" />
      </div>
    );
  }

  if (error?.response?.status === 404) {
    return (
      <div>
        <BackLink />
        <EmptyState
          title="This offer is no longer available"
          description="The office has taken it down. Every other offer, running or ended, is under All offers."
        />
      </div>
    );
  }

  if (isError || !offer) {
    return (
      <div>
        <BackLink />
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  const reward = offerReward(offer);
  const target = offerTarget(offer);
  const closes = offerDeadline(offer);
  const images = offer.imageUrls || [];

  return (
    <div>
      <BackLink />
      <PageHeader
        title={offer.title}
        actions={offer.ended ? <StatusChip>Ended</StatusChip> : null}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <Fact label="Reward" value={reward} />
        <Fact label="Target" value={target} />
        <Fact
          label={offer.ended ? "Ended on" : "Open until"}
          value={closes || (offer.ended ? "Ended" : "No end date")}
        />
      </div>

      {images.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          {images.map((url) => (
            <img
              key={url}
              src={url}
              alt=""
              className="w-full rounded-xl border border-slate-200 object-cover"
            />
          ))}
        </div>
      ) : null}

      <Panel title="Details">
        {offer.description ? (
          <p className="text-sm text-slate-700 m-0 whitespace-pre-line break-words leading-relaxed">
            {offer.description}
          </p>
        ) : (
          <p className="text-sm text-slate-500 m-0">The office did not add any further details.</p>
        )}
      </Panel>
    </div>
  );
};

export default OfferDetailPage;
